import {RefObject, useCallback, useEffect, useRef} from 'react';
import {DirectionalLight, LightProbe, WebGLCubeRenderTarget, WebGLRenderer} from 'three';
import {useFrame, useThree} from '@react-three/fiber';
import {xrStore} from 'lib';

const useLightEstimate = ({
  lightProbeElRef,
  directionalLightElRef,
}: {
  lightProbeElRef: RefObject<LightProbe | null>;
  directionalLightElRef: RefObject<DirectionalLight | null>;
}) => {
  const gl = useThree(s => s.gl);
  const scene = useThree(s => s.scene);
  const lightProbeRef = useRef<LightProbe>(null);
  const cubeTargetRef = useRef(new WebGLCubeRenderTarget(16));

  const handleReflectionChanged = useCallback((glBinding: any, renderer: WebGLRenderer) => {
    const glCubeMap = glBinding.getReflectionCubeMap(lightProbeRef.current);
    if (glCubeMap) {
      const glProps = renderer.properties.get(cubeTargetRef.current.texture) as any;
      glProps.__webglTexture = glCubeMap;
      cubeTargetRef.current.texture.needsPMREMUpdate = true;
    }
  }, []);

  useEffect(() => {
    scene.environment = cubeTargetRef.current.texture;
  }, [scene]);

  useEffect(() => {
    let listener: Function | undefined;
    (async () => {
      const session = xrStore.getState().session as any;
      if (!session) {
        return;
      }
      lightProbeRef.current = await session.requestLightProbe({
        reflectionFormat: session.preferredReflectionFormat,
      });
      if (!lightProbeRef.current) {
        return;
      }
      const ctx = gl.getContext();
      switch (session.preferredReflectionFormat) {
        case 'srgba8':
          ctx.getExtension('EXT_sRGB');
          break;
        case 'rgba16f':
          ctx.getExtension('OES_texture_half_float');
          break;
      }
      const glBinding = new XRWebGLBinding(session, ctx);
      listener = () => handleReflectionChanged(glBinding, gl);
      // @ts-expect-error
      lightProbeRef.current.addEventListener('reflectionchange', listener);
    })();
    return () => {
      if (listener && lightProbeRef.current) {
        // @ts-expect-error
        lightProbeRef.current.removeEventListener('reflectionchange', listener);
      }
    };
  }, [gl, handleReflectionChanged]);

  useFrame((_s, _d, frame) => {
    if (!frame || !lightProbeRef.current || !lightProbeElRef.current || !directionalLightElRef.current) {
      return;
    }
    const ambientLightEstimate = (frame as any).getLightEstimate(lightProbeRef.current);
    if (!ambientLightEstimate) {
      return;
    }
    lightProbeElRef.current.intensity = 1;
    lightProbeElRef.current.sh.fromArray(ambientLightEstimate.sphericalHarmonicsCoefficients);
    const intensity = Math.max(
      1.0,
      ambientLightEstimate.primaryLightIntensity.x,
      ambientLightEstimate.primaryLightIntensity.y,
      ambientLightEstimate.primaryLightIntensity.z,
    );
    directionalLightElRef.current.intensity = intensity;
    directionalLightElRef.current.color.setRGB(
      ambientLightEstimate.primaryLightIntensity.x / intensity,
      ambientLightEstimate.primaryLightIntensity.y / intensity,
      ambientLightEstimate.primaryLightIntensity.z / intensity,
    );
  });
};

export default useLightEstimate;
